import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { qk } from '@/api/queries';
import type { PresetDetail } from '@/api/types';
import { Button, Icon, IconButton, Input, Modal, Segmented, SECTION_TYPES, useToast, type IconName } from '@/design-system';
import { ICONS } from '@/design-system/icons';
import { cx } from '@/lib/cx';
import { errorText } from '@/shell/errorText';
import { addSectionType, createDrafts, sectionTypesQuery, uploadReference } from './api';
import { usePresetLabels } from './labels';
import s from './NewPresetModal.module.css';

/** The MD preview shows this many characters (design: fileT.slice(0, 1200)). */
const PREVIEW_CHARS = 1200;

type Photo = { file: File; url: string };
type MdFile = { name: string; text: string; size: number };

function iconOf(icon: string): IconName {
  const name = icon.replace(/^lucide:/, '');
  return name in ICONS ? (name as IconName) : 'shapes';
}

/**
 * «Новый пресет» (DESIGN_SPEC §4.7, 31_newpreset; P3-D02 `POST /admin/presets/drafts`): a name, one or
 * more section types (an Admin may add a custom one), reference photos and a text or .md prompt become
 * one draft per type. Photos upload to the library on create (`POST /admin/presets/media`).
 */
export function NewPresetModal({ onClose, onCreated }: { onClose: () => void; onCreated?: (drafts: PresetDetail[]) => void }) {
  const { t } = useTranslation('library');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const titleId = useId();
  const labels = usePresetLabels();
  const typesQ = useQuery(sectionTypesQuery);
  const typeRows = typesQ.data?.map((x) => ({ key: x.key, icon: iconOf(x.icon) })) ?? SECTION_TYPES.map((x) => ({ key: x.key, icon: x.icon }));

  const [name, setName] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [newType, setNewType] = useState('');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [mode, setMode] = useState<'text' | 'md'>('text');
  const [prompt, setPrompt] = useState('');
  const [md, setMd] = useState<MdFile | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // The object URLs of the photo previews go with the photos.
  const urls = useRef<string[]>([]);
  urls.current = photos.map((p) => p.url);
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const promptText = mode === 'md' ? (md?.text ?? '') : prompt;
  const ok = types.length > 0 && (!!promptText.trim() || photos.length > 0);

  const toggle = (key: string) => setTypes((x) => (x.includes(key) ? x.filter((k) => k !== key) : [...x, key]));

  const addType = useMutation({
    mutationFn: (label: string) => addSectionType(label),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: qk.sectionTypes });
      setTypes((x) => [...x.filter((k) => k !== row.key), row.key]);
      setNewType('');
      toast(t('modal.type_added', { name: row.name_ru }));
    },
    onError: (err, label) => {
      // «Нет нужной?» with an existing name just picks that type (design: addType).
      const same = typesQ.data?.find((x) => x.name_ru.toLowerCase() === label.toLowerCase() || x.name_uz.toLowerCase() === label.toLowerCase());
      if (err instanceof ApiError && same) {
        setTypes((x) => [...x.filter((k) => k !== same.key), same.key]);
        setNewType('');
        return;
      }
      setProblem(errorText(err, tc));
    },
  });
  const submitType = () => {
    const label = newType.trim();
    if (!label || addType.isPending) return;
    const same = typeRows.find((x) => labels.section(x.key).toLowerCase() === label.toLowerCase());
    if (same) {
      setTypes((x) => [...x.filter((k) => k !== same.key), same.key]);
      setNewType('');
      return;
    }
    addType.mutate(label);
  };

  const create = useMutation({
    mutationFn: async () => {
      const media: string[] = [];
      for (const p of photos) media.push((await uploadReference(p.file)).id);
      return createDrafts({ name: name.trim() || t('modal.default_name'), section_types: types, prompt: promptText.trim() || null, reference_media: media });
    },
    onSuccess: (drafts) => {
      void qc.invalidateQueries({ queryKey: ['presets'] });
      void qc.invalidateQueries({ queryKey: qk.sectionTypes });
      toast(t('modal.created', { count: drafts.length }));
      onCreated?.(drafts);
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });

  const pickPhotos = (files: FileList | null) => {
    const add = [...(files ?? [])].filter((f) => f.type.startsWith('image/')).map((file) => ({ file, url: URL.createObjectURL(file) }));
    setPhotos((x) => [...x, ...add]);
  };
  const removePhoto = (i: number) =>
    setPhotos((x) => {
      const gone = x[i];
      if (gone) URL.revokeObjectURL(gone.url);
      return x.filter((_, j) => j !== i);
    });
  const pickMd = (file: File | undefined) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setMd({ name: file.name, text: String(r.result ?? ''), size: file.size });
    r.readAsText(file);
  };
  const size = (n: number) => (n < 1024 ? t('modal.bytes', { n }) : t('modal.kilobytes', { n: (n / 1024).toFixed(1).replace('.', ',') }));

  return (
    <Modal open onClose={onClose} variant="sheet" labelledBy={titleId}>
      <div className={s.header}>
        <div className={s.titles}>
          <h2 id={titleId} className={s.title}>
            {t('modal.title')}
          </h2>
          <span className={s.sub}>{t('modal.sub')}</span>
        </div>
        <IconButton icon="x" label={tc('actions.close_esc')} tone="outline" size={36} onClick={onClose} />
      </div>
      <div className={s.body}>
        <label className={s.field}>
          <span className={s.caps}>{t('modal.name')}</span>
          <Input height={44} value={name} placeholder={t('modal.name_placeholder')} onChange={(e) => setName(e.target.value)} />
        </label>

        <div className={s.group}>
          <div className={s.groupHead}>
            <span className={s.caps}>{t('modal.types')}</span>
            <span className={s.count}>{types.length ? t('modal.types_count', { n: types.length }) : t('modal.types_none')}</span>
          </div>
          <div className={s.chips} role="group" aria-label={t('modal.types')}>
            {typeRows.map((x) => {
              const on = types.includes(x.key);
              return (
                <button key={x.key} type="button" className={cx(s.chip, on && s.on)} aria-pressed={on} onClick={() => toggle(x.key)}>
                  <Icon name={on ? 'check' : x.icon} size={13} />
                  {labels.section(x.key)}
                </button>
              );
            })}
          </div>
          <div className={s.newTypeRow}>
            <div className={s.newType}>
              <Icon name="plus" size={14} className={s.newTypeIcon} />
              <input
                className={s.newTypeInput}
                value={newType}
                placeholder={t('modal.new_type')}
                aria-label={t('modal.new_type')}
                onChange={(e) => setNewType(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitType();
                  }
                }}
              />
              <button type="button" className={s.newTypeAdd} onClick={submitType} disabled={addType.isPending}>
                {t('modal.new_type_add')}
              </button>
            </div>
          </div>
        </div>

        <div className={s.group}>
          <div className={s.groupHead}>
            <span className={s.caps}>{t('modal.photos')}</span>
            <span className={s.count}>{photos.length ? t('modal.photos_count', { n: photos.length }) : t('modal.optional')}</span>
          </div>
          <div className={s.photos}>
            {photos.map((p, i) => (
              <div key={p.url} className={s.photo}>
                <div className={s.photoImg} title={p.file.name} style={{ background: `url(${p.url}) center/cover no-repeat` }} />
                <button type="button" className={s.photoRemove} title={t('modal.remove')} aria-label={`${t('modal.remove')}: ${p.file.name}`} onClick={() => removePhoto(i)}>
                  <Icon name="x" size={12} />
                </button>
              </div>
            ))}
            <label className={s.addPhoto}>
              <Icon name="image-plus" size={20} />
              {t('modal.add_photo')}
              <input
                type="file"
                accept="image/*"
                multiple
                className={s.hiddenInput}
                aria-label={t('modal.add_photo')}
                onChange={(e) => {
                  pickPhotos(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>

        <div className={s.group}>
          <div className={s.groupHeadCenter}>
            <span className={s.caps}>{t('modal.prompt')}</span>
            <Segmented
              label={t('modal.prompt')}
              value={mode}
              onChange={setMode}
              className={s.modeSeg}
              options={[
                { value: 'text', label: t('modal.mode_text') },
                { value: 'md', label: t('modal.mode_md') },
              ]}
            />
          </div>
          {mode === 'text' ? (
            <textarea className={s.prompt} rows={6} value={prompt} placeholder={t('modal.prompt_placeholder')} aria-label={t('modal.prompt')} onChange={(e) => setPrompt(e.target.value)} />
          ) : md ? (
            <div className={s.mdBox}>
              <div className={s.mdHead}>
                <Icon name="file-text" size={15} className={s.mdIcon} />
                <span className={s.mdName}>{md.name}</span>
                <span className={s.mdSize}>{size(md.size)}</span>
                <button type="button" className={s.mdRemove} title={t('modal.remove')} aria-label={`${t('modal.remove')}: ${md.name}`} onClick={() => setMd(null)}>
                  <Icon name="x" size={12} />
                </button>
              </div>
              <pre className={s.mdPreview}>{md.text.slice(0, PREVIEW_CHARS) + (md.text.length > PREVIEW_CHARS ? '\n…' : '')}</pre>
            </div>
          ) : (
            <label className={s.mdPick}>
              <Icon name="file-text" size={18} />
              {t('modal.md_pick')}
              <input
                type="file"
                accept=".md,.markdown,.txt,text/markdown,text/plain"
                className={s.hiddenInput}
                aria-label={t('modal.md_pick')}
                onChange={(e) => {
                  pickMd(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
      </div>
      <div className={s.footer}>
        <span className={cx(s.hint, ok && s.hintOk, problem && s.hintErr)} role={problem ? 'alert' : undefined}>
          {problem ?? (ok ? t('modal.hint_ok', { n: types.length }) : t('modal.hint'))}
        </span>
        <Button variant="secondary" onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        <Button icon="sparkles" iconSize={15} disabled={!ok || create.isPending} onClick={() => create.mutate()}>
          {t('modal.create', { count: Math.max(1, types.length) })}
        </Button>
      </div>
    </Modal>
  );
}
